import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import generalService from "../../../../service/general-service";
import { getProduct } from "../../../../constant/products";
import BaiyeReportIndex from "./baiye-report-index";
import BaiyeReportOutro from "./baiye-report-outro";
import "./baiye-report-style.scss";

//—— 白夜・八字批命：命書 ——
//
//  一本書，不是一頁長文。結構：
//    封面 ×1 → （章首頁 + 三個小節頁）×9 = 37 頁
//
//  翻頁而不是捲到底：一節一千八百字，九章捲完要捲很久，
//  而且捲動沒有「讀到哪」的概念 —— 翻頁才有進度、才記得住、
//  才做得出格狀目錄裡「你上次停在這一頁」。
//
//  —— 章名從哪裡來 ——
//  章名與小節名走 API（G-4），不在這裡寫死。那份清單同時是給 AI 的
//  產出規格（engines/baiye-natal/chapters），前端再存一份就會分叉，
//  而分叉的症狀是「畫面寫 A、內文講的是 B」。
//
//  props:
//    content  AI 產出的 { chapters: { 章key: { opening, sections[] } } }
//    bookID   記進度用的識別（給 orderID）。省略就不記
//    reviewed 這張單已經評價過了（收尾頁用）
const PROGRESS_PREFIX = "baiye-report-page:";
//真的翻開過哪些頁（頁碼陣列）。跟上面那個不同 ——
//  上面是「回來時要翻到哪」，這個是「目錄上的進度」。
//
//  記「哪些頁」而不是「最遠到哪一頁」：目錄可以直接跳章，
//  用最遠值的話從第一章跳到第五章，中間三章會全部被記成讀完 ——
//  那三章連打開都沒打開過。
const READ_PREFIX = "baiye-report-read:";

//下載成 PDF 的按鈕要不要顯示。
//  功能是完整的（列印時會把 38 頁一次攤開，列印樣式也寫好了），
//  只是還沒在真機上驗過分頁在哪裡斷、插圖會不會被切，所以先收起來。
const SHOW_DOWNLOAD = false;

//—— 工具列的收放門檻 ——
//  SCROLL_DELTA：方向要變更多少距離才算「真的換方向」。
//    手指離開螢幕時幾乎都會有一兩 px 的反向抖動，
//    不設門檻的話那一下會把收起來的工具列又叫回來。
//  SCROLL_TOP_ZONE：這段距離內一律當成在頁首，工具列固定顯示。
const SCROLL_DELTA = 10;
const SCROLL_TOP_ZONE = 24;

//封面。整張圖，不壓書名與章節數 —— 那些翻開之後都看得到，
//  封面的工作是讓人先看到他一眼。
const COVER_IMAGE = `${process.env.PUBLIC_URL}/images/baiye/report/baiye-report-1.jpg`;

//章首頁的底圖。跟故事頁那九張章節卡是同一張 ——
//  使用者在付款前看到的那九張卡，付款後翻開就是同一個東西，
//  換一張底圖會讓人以為買到的跟看到的不一樣。
const CHAPTER_BG = `${process.env.PUBLIC_URL}/images/baiye/baiye-story-bg-4.jpg`;

//收尾頁的底圖。跟章首頁不同一張 —— 那是「還有下一章」的火焰，
//  這裡是整本讀完了，要換一個收得住的畫面。
const OUTRO_BG = `${process.env.PUBLIC_URL}/images/baiye/baiye-story-bg-1.jpg`;

//「第 N 章」的中文數字。九章寫死就夠，不為了九個字引一套轉換。
const CH_NO = ["一", "二", "三", "四", "五", "六", "七", "八", "九"];

//每一節插圖的網址。檔名由章節表指定（chapters.js 的 art）——
//  不由 key 推導、也不用「載不到再退回占位」，理由見那裡的 ⚠️。
const artUrl = (file) =>
  `${process.env.PUBLIC_URL}/images/baiye/report/${file}`;

//疊在插圖上的對話框可以擺的四個位置。
//
//  一節兩顆框：固定那顆由章節表指定（bubbleSpot），AI 那顆去它的對角
//  —— 兩顆在同一側會疊在一起，對角才拉得開。
//
//  ⚠️ top 不能亂加：圖框是 overflow: hidden，框超出去會被切成半個橢圓。
//  橢圓的高度不是獨立的數字，是由寬度推出來的（aspect-ratio 7/10）：
//
//    橢圓高（佔圖框高的比例）= 寬 × 10/7 ÷ 1.25        ← 1.25 是圖框的 4:5
//                            = 寬 × 1.143
//
//  所以 top + 寬 × 1.143 必須 ≤ 100%，left + 寬 必須 ≤ 100%。
//  四個位置都算過（下緣最多 94%、右緣最多 92%）。
const BUBBLE_SPOTS = {
  "left-mid": { top: "33%", left: "6%", width: "30%" },
  "right-mid": { top: "33%", left: "60%", width: "30%" },
  "left-low": { top: "52%", left: "8%", width: "37%" },
  "right-low": { top: "52%", left: "55%", width: "37%" },
};

//固定那顆在這裡，AI 那顆就去對面。對角配對而不是左右配對 ——
//  同高度的兩顆會像一副眼鏡，對角才有「一來一往」的感覺。
const BUBBLE_PAIR = {
  "left-mid": "right-low",
  "right-low": "left-mid",
  "right-mid": "left-low",
  "left-low": "right-mid",
};

const DEFAULT_SPOT = "left-mid";

//把「框寬佔圖的百分比」換算成字級，作法同故事頁的對話框。
//  0.16：對話框上限十四個字、通常斷成兩三行。故事頁同樣字數的框是
//  0.168～0.205，這裡再收一點 —— 那邊的框是疊在滿版的圖上，
//  這裡的圖只佔頁面一部分，同樣比例看起來會偏大。
const bubbleFontSize = (width) => `${parseFloat(width) * 0.16}cqw`;

//把章節表 + 內容攤成一串頁。
//  攤平而不是巢狀走訪：翻頁、左右滑、目錄跳轉全都只是在改一個 index，
//  巢狀的話每個動作都要處理「這一章翻完了要跳下一章」的邊界。
const buildPages = (chapters, content) => {
  const pages = [{ type: "cover" }];

  chapters.forEach((ch) => {
    const written = content?.chapters?.[ch.key];
    if (!written) return;

    pages.push({ type: "chapter", ch, opening: written.opening });

    ch.sections.forEach((sec) => {
      //內容是用 key 對回來的，不是照順序 —— AI 回傳的陣列順序沒有保證，
      //  照順序配的話會出現「標題是感情、內文在講錢」。
      const body = (written.sections || []).find((x) => x.key === sec.key);
      if (!body) return;
      pages.push({ type: "section", ch, sec, ...body });
    });
  });

  //最後一頁：收尾。只有真的有章節畫出來時才掛 ——
  //  內容還沒好的時候整本是空的，這時放一頁「你的命書，到這裡」很怪。
  if (pages.length > 1) pages.push({ type: "outro" });

  return pages;
};

//一節的內容是 block 陣列（heading / text / table），不是一整串字。
//  理由見 baiye-natal-engine 的 SECTION_SCHEMA：一節兩千字，
//  沒有小標就是一堵牆，讀不下去。
//
//  後端已經把壞掉的 block 濾掉了（section-runner 的 cleanBlocks），
//  這裡再防一次是因為舊報告可能是別的形狀 —— 畫不出來就跳過，
//  不要讓整本書因為一個格式不對的 block 整個白掉。
const renderBlock = (b, i) => {
  if (!b || typeof b !== "object") return null;

  if (b.type === "heading" && b.text) {
    return (
      <h3 key={i} className="baiye-report__h3">
        {b.text}
      </h3>
    );
  }

  if (b.type === "text" && b.text) {
    return (
      <p key={i} className="baiye-report__para">
        {b.text}
      </p>
    );
  }

  if (b.type === "table" && b.columns?.length && b.rows?.length) {
    return (
      <div key={i} className="baiye-report__table-wrap">
        <table className="baiye-report__table">
          <thead>
            <tr>
              {b.columns.map((c, x) => (
                <th key={x}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {b.rows.map((row, y) => (
              <tr key={y}>
                {row.map((cell, x) => (
                  <td key={x}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return null;
};

//滑動超過這個距離才翻頁，避免手指輕輕一碰就跳走（數值同劇情頁）
const SWIPE_THRESHOLD = 40;

//一節的插圖。圖還沒進來之前畫一個占位框，上面寫出它在等哪一個檔案 ——
//  空白的框只會讓人以為版面壞了，寫出檔名才知道要放什麼進去。
//  兩顆對話框分別從哪來：
//    固定的  章節表（chapters.js 的 bubble）—— 配合這張圖的構圖
//    AI 的   SECTION_SCHEMA 的 bubble —— 扣住這個人的命盤
//
//  固定那顆擺哪由章節表的 bubbleSpot 決定，AI 那顆去它的對角。
//  任一顆沒有就只畫另一顆，位置不補位 —— 指定在右中就該在右中，
//  補到別處會跟那張圖的構圖對不上（那正是要逐節指定位置的原因）。
const SectionArt = ({ sec, bubble }) => {
  const spot = BUBBLE_SPOTS[sec.bubbleSpot] ? sec.bubbleSpot : DEFAULT_SPOT;
  //  bubbleTop 只微調固定那一顆的高度，側邊與大小仍照 bubbleSpot。
  //  AI 那顆照它的對角位置，不跟著動 —— 兩顆一起移就失去「對角拉開」的意義。
  const bubbles = [
    [spot, sec.bubble, sec.bubbleTop],
    [BUBBLE_PAIR[spot], bubble, ""],
  ];

  return (
    <div className="baiye-report__art">
      {sec.art ? (
        <img className="baiye-report__art-img" src={artUrl(sec.art)} alt="" />
      ) : (
        <div className="baiye-report__art-todo">
          <span>插圖預留位置</span>
          <code>{sec.title}</code>
        </div>
      )}

      {bubbles.map(([at, text, top]) =>
        text ? (
          <span
            key={at}
            className="baiye-report__art-bubble"
            style={{
              ...BUBBLE_SPOTS[at],
              ...(top ? { top } : null),
              fontSize: bubbleFontSize(BUBBLE_SPOTS[at].width),
            }}
          >
            {text}
          </span>
        ) : null,
      )}
    </div>
  );
};

const BaiyeReport = ({ content, bookID, reviewed }) => {
  const navigate = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [index, setIndex] = useState(0);
  //往前翻還是往後翻。進場動畫要照這個方向橫移 ——
  //  不分方向的話，按「上一頁」看起來還是從右邊推進來，像又翻了一頁。
  const [dir, setDir] = useState(1);
  const [showIndex, setShowIndex] = useState(false);
  //翻開過的頁碼。目錄的進度條看這個，不看 index ——
  //  只看 index 的話，一離開第一章，第一章就又變回「尚未開始」。
  const [visited, setVisited] = useState(() => new Set());
  //複製連結之後的提示。兩秒後自己消失
  const [toast, setToast] = useState("");
  //上下兩條工具列現在顯示著沒有。下滑時收起來，把整個畫面讓給內文；
  //  上滑（想回頭、想翻頁）才讓它們回來。
  const [chromeOn, setChromeOn] = useState(true);
  const touchStartX = useRef(null);
  const bodyRef = useRef(null);
  //上一次的捲動位置。判斷方向用的，不需要重畫，所以放 ref 不放 state。
  const lastScrollY = useRef(0);

  //章節表。抓失敗就不畫 —— 沒有章名的報告只是一堆沒有標題的長文，
  //  與其畫成那樣，不如讓外層維持等待狀態。
  useEffect(() => {
    let alive = true;
    generalService
      .baiyeChapters()
      .then(({ data }) => {
        if (alive && data?.status) setChapters(data.data.chapters || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const pages = useMemo(
    () => buildPages(chapters, content),
    [chapters, content],
  );

  //—— 讀到哪一頁 ——
  //  存 localStorage 而不是後端：它是「這台裝置上的閱讀習慣」，
  //  不是訂單的一部分。換裝置會從頭開始，但換裝置的人本來就會重新找位置。
  //  key 綁 bookID（orderID），同一台裝置買兩份不會互相覆蓋。
  //
  //  restored 這道閘門是必要的。chapters 走 API，所以第一輪 render 時
  //  pages 只有封面一頁 —— 這時候還原讀不到（頁碼超出範圍），
  //  但底下那個存檔的 effect 照樣會把 index 0 寫進去，
  //  上次的進度就被蓋成第一頁了。等書排好、還原過，才准開始存。
  const restored = useRef(false);

  useEffect(() => {
    //pages.length <= 1 表示只有封面，書還沒排好
    if (restored.current || !bookID || pages.length <= 1) return;
    restored.current = true;
    try {
      const saved = Number(localStorage.getItem(PROGRESS_PREFIX + bookID));

      //  —— 讀完的書要從第一章開啟，不是從收尾頁 ——
      //  buildPages 的最後一頁是 outro（收尾：留評價、聯絡客服、回首頁）。
      //  讀到底的人進度就停在那裡，下次從收藏庫點進來會直接落在收尾頁 ——
      //  等於「我想再看一次」換來的是一張結束畫面。
      //
      //  回第 1 頁而不是第 0 頁：0 是封面，那是開書的儀式、只該看一次。
      //  1 是第一章的扉頁，才是內容真正的起點。
      //
      //  判斷用「停在最後一頁」而不是「每一頁都讀過」：從目錄直接跳到
      //  收尾頁的人其實沒讀完，但他下次也該從頭開始 —— 兩種情況要的是同一件事。
      const finished = saved === pages.length - 1;
      const openAt = finished ? 1 : saved;
      if (openAt > 0 && openAt < pages.length) setIndex(openAt);

      const raw = localStorage.getItem(READ_PREFIX + bookID);
      const list = raw ? JSON.parse(raw) : null;
      if (Array.isArray(list)) {
        //合併而不是覆蓋 —— 這一輪已經翻開的頁（至少有封面）不能被丟掉
        setVisited(
          (prev) =>
            new Set([
              ...prev,
              ...list.filter(
                (n) => Number.isInteger(n) && n >= 0 && n < pages.length,
              ),
            ]),
        );
      }
    } catch (e) {
      //隱私模式、封鎖 cookie、存進去的 JSON 壞掉都會走到這裡。
      //  記不住進度不影響閱讀，所以不做任何處理。
    }
  }, [bookID, pages.length]);

  //翻到哪一頁就把那一頁記成讀過。沒變就回原本那個 Set ——
  //  每次都 new 一個的話，底下存檔的 effect 會被無限觸發。
  useEffect(() => {
    setVisited((s) => (s.has(index) ? s : new Set(s).add(index)));
  }, [index]);

  useEffect(() => {
    if (!restored.current || !bookID) return;
    try {
      localStorage.setItem(PROGRESS_PREFIX + bookID, String(index));
      localStorage.setItem(READ_PREFIX + bookID, JSON.stringify([...visited]));
    } catch (e) {
      //同上
    }
  }, [bookID, index, visited]);

  const go = useCallback(
    (next) => {
      setIndex((i) => {
        const to = Math.min(Math.max(next ?? i, 0), pages.length - 1);
        //原地不動就不要改方向，否則按到底的那一下會莫名反向
        if (to !== i) setDir(to > i ? 1 : -1);
        //換頁要把捲軸拉回頂端 —— 不拉的話從長頁翻到短頁會停在空白處
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
        return to;
      });
    },
    [pages.length],
  );

  //—— 下滑收起、上滑叫回工具列 ——
  //  比對這次與上次的 scrollTop 來判方向。兩個例外一律顯示：
  //  頁首那一段（還沒開始讀），以及捲到底（一節讀完了，接下來要按「下一頁」——
  //  這時候把翻頁鈕藏起來最惱人）。捲到底也順手擋掉 iOS 回彈
  //  那一下反向位移造成的閃現。
  const onBodyScroll = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;

    const y = el.scrollTop;
    const prev = lastScrollY.current;
    const atTop = y <= SCROLL_TOP_ZONE;
    const atBottom = el.scrollHeight - y - el.clientHeight <= 4;

    if (atTop || atBottom) {
      lastScrollY.current = y;
      setChromeOn(true);
      return;
    }

    //位移還不夠大就先不動，也不要更新基準點 ——
    //  更新了的話慢慢捲永遠累積不到門檻，等於整個判斷失效。
    if (Math.abs(y - prev) < SCROLL_DELTA) return;

    lastScrollY.current = y;
    setChromeOn(y < prev);
  }, []);

  //換頁時工具列要回來。捲軸已經被 go() 拉回頂端了，
  //  方向的基準點也要跟著歸零，否則下一頁的第一次捲動會跟上一頁的位置比。
  useEffect(() => {
    lastScrollY.current = 0;
    setChromeOn(true);
  }, [index]);

  //鍵盤左右鍵。桌機上看的人會下意識按方向鍵
  useEffect(() => {
    const onKey = (e) => {
      if (showIndex) return;
      if (e.key === "ArrowRight") go(index + 1);
      if (e.key === "ArrowLeft") go(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, index, showIndex]);

  //複製連結的提示，兩秒後自己消失
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(""), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  //—— 下載成 PDF ——
  //
  //  用瀏覽器的列印（另存為 PDF）而不是裝 PDF 套件：
  //  jsPDF + html2canvas 會把整本書轉成點陣圖（文字不能選、不能搜尋、
  //  中文還要另外嵌字型，bundle 大好幾 MB），而列印出來的是真的文字。
  //  手機上 iOS 與 Android 的列印面板都有「儲存為 PDF」。
  //
  //  ⚠️ 關鍵：這個閱讀器一次只掛一頁（key={index}），直接列印只會印到
  //  當下那一頁。所以要先把 38 頁全部畫出來，等畫面更新完才叫 print()。
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!printing) return undefined;
    //等這一次 render 真的進到畫面上再列印 —— 直接呼叫會印到舊的 DOM
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => window.print()),
    );
    const done = () => setPrinting(false);
    window.addEventListener("afterprint", done);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("afterprint", done);
    };
  }, [printing]);

  //—— 分享 ——
  //  有原生分享面板就用它（手機上使用者認得那個面板），
  //  沒有就退回複製網址 —— 桌機瀏覽器多數不支援 navigator.share。
  //—— 分享 ——
  //  分享出去的是「產品頁」，不是這一頁。
  //  這一頁的網址帶著 orderID，等於把自己的命書連結交出去 ——
  //  收到的人點進來讀到的是你的命，不是他的。
  //
  //  host 取 window.location.origin，不寫死也不另外設環境變數：
  //  人在哪個站上讀，分享出去就是那個站 —— 在正式站分享正式站，
  //  在 localhost / 測試站分享的就是那個網址（測試站有 Basic Auth，
  //  外人開不了，但在測試站按分享本來也只是為了試功能）。
  const share = async () => {
    const path = getProduct("bazi-baiye")?.path || "/";
    const data = {
      title: "白夜・八字批命",
      text: "白夜替你批一份命書。",
      url: window.location.origin + path,
    };
    try {
      if (navigator.share) {
        await navigator.share(data);
        return;
      }
      await navigator.clipboard.writeText(data.url);
      setToast("連結已複製");
    } catch (e) {
      //使用者自己取消分享也會走到這裡，不是錯誤，不要跳任何東西
    }
  };

  if (pages.length <= 1) return null;

  const page = pages[index];
  const atFirst = index === 0;
  const atLast = index === pages.length - 1;

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    const start = touchStartX.current;
    touchStartX.current = null;
    if (start == null) return;
    const dx = e.changedTouches[0].clientX - start;
    if (Math.abs(dx) > SWIPE_THRESHOLD) go(index + (dx < 0 ? 1 : -1));
  };

  //畫一頁。抽成函式是為了列印 —— 那時要一次把 38 頁全部畫出來，
  //  平常只畫當下這一頁（見 printing）。
  const renderPage = (page) => (
    <>
      {page.type === "cover" && (
        <section className="baiye-report__cover">
          {/* alt 留空：這是裝飾，不是資訊。讀屏唸一遍「白夜的插畫」
              對看不見的人沒有幫助，只是多一個要跳過的東西。
              書名與章節數在下一頁（章首頁）就讀得到。 */}
          <img className="baiye-report__cover-img" src={COVER_IMAGE} alt="" />
        </section>
      )}

      {page.type === "chapter" && (
        //版型與故事頁的章節卡完全一致（見 .baiye-story__chapter）——
        //  那九張卡是付款前賣出去的承諾，翻開之後要是同一個東西。
        <section
          className="baiye-report__chapter"
          style={{ "--chapter-bg": `url(${CHAPTER_BG})` }}
        >
          <div className="baiye-report__chapter-inner">
            <p className="baiye-report__chapter-badge">
              第{CH_NO[page.ch.no - 1] || page.ch.no}章
            </p>
            <h2 className="baiye-report__chapter-title">{page.ch.title}</h2>

            {/* 這三條就是使用者在故事頁看到、然後掏錢買的那三行 ——
                一字不差地再出現一次。 */}
            {page.ch.sections.map((sec) => (
              <p className="baiye-report__chapter-item" key={sec.key}>
                <span className="baiye-report__chapter-mark" aria-hidden="true">
                  ◆
                </span>
                {sec.title}：{sec.brief}
              </p>
            ))}
          </div>
        </section>
      )}

      {page.type === "outro" && (
        <BaiyeReportOutro
          bookID={bookID}
          reviewed={reviewed}
          bg={OUTRO_BG}
        />
      )}

      {page.type === "section" && (
        <section className="baiye-report__section">
          {/* 小節頁左上角的章號。跟章首頁那顆大膠囊是同一個東西的縮小版，
              作用是「你現在在第幾章」—— 連翻三節之後人會忘記。 */}
          <span className="baiye-report__tag">第{page.ch.no}章</span>
          <h2 className="baiye-report__section-title">{page.sec.title}</h2>
          <p className="baiye-report__section-brief">{page.sec.brief}</p>

          <SectionArt sec={page.sec} bubble={page.bubble} />

          {(page.blocks || []).map(renderBlock)}
        </section>
      )}
    </>
  );

  return (
    <div
      className="baiye-report"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* 頂部列。浮在內容上（同底部控制列），所以滿版的封面與章首頁
          仍然能貼到畫面最上緣。列印時整條不印（見 scss 的 @media print）。 */}
      <div
        className={`baiye-report__top${chromeOn ? "" : " is-off"}`}
      >
        <button
          type="button"
          className="baiye-report__top-btn"
          onClick={() => navigate("/")}
          aria-label="回首頁"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              d="M3.5 10.6 12 4l8.5 6.6V20a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <span className="baiye-report__top-title">白夜・八字批命</span>

        {/* 下載 PDF 先收起來。版面與列印樣式都做好了（見 printing 與
            scss 的 @media print），實機驗過分頁與插圖沒問題再把這塊打開。
            SHOW_DOWNLOAD 改成 true 就會回來。 */}
        {SHOW_DOWNLOAD && (
          <button
            type="button"
            className="baiye-report__top-btn"
            onClick={() => setPrinting(true)}
            aria-label="下載 PDF"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <path
                d="M12 3v11m0 0 4-4m-4 4-4-4M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        <button
          type="button"
          className="baiye-report__top-btn"
          onClick={share}
          aria-label="分享"
        >
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              d="M8.6 13.4a3 3 0 1 1 0-2.8m0 2.8 6.8 3.4m-6.8-6.2 6.8-3.4M18 7.5a3 3 0 1 1 0-.1zm0 9a3 3 0 1 1 0-.1z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* key 綁 index：翻頁時整頁重新掛載，進場動畫才會重播。
          也順便避開「疊在影片上的常駐節點在 iOS 會卡半透明」那一類問題。

          列印時改成一次畫 38 頁 —— 閱讀器平常只掛當下那一頁，
          不攤開的話另存出來的 PDF 只有一頁（見 printing）。 */}
      <div
        className={`baiye-report__body${dir < 0 ? " is-prev" : ""}`}
        ref={bodyRef}
        onScroll={onBodyScroll}
        key={printing ? "print" : index}
      >
        {printing
          ? pages.map((p, i) => (
              <div className="baiye-report__sheet" key={i}>
                {renderPage(p)}
              </div>
            ))
          : renderPage(page)}
      </div>

      {toast && <p className="baiye-report__toast">{toast}</p>}

      {/* 底部的浮動控制列：翻頁 + 開目錄 */}
      <div className={`baiye-report__bar${chromeOn ? "" : " is-off"}`}>
        <button
          type="button"
          className="baiye-report__nav"
          onClick={() => go(index - 1)}
          disabled={atFirst}
          aria-label="上一頁"
        >
          ‹
        </button>
        <button
          type="button"
          className="baiye-report__nav"
          onClick={() => go(index + 1)}
          disabled={atLast}
          aria-label="下一頁"
        >
          ›
        </button>

        <span className="baiye-report__count">
          {index + 1} / {pages.length}
        </span>

        <button
          type="button"
          className="baiye-report__grid"
          onClick={() => setShowIndex(true)}
          aria-label="章節目錄"
        >
          <span /> <span /> <span /> <span />
        </button>
      </div>

      {showIndex && (
        <BaiyeReportIndex
          chapters={chapters}
          pages={pages}
          index={index}
          visited={visited}
          onPick={(to) => {
            go(to);
            setShowIndex(false);
          }}
          onClose={() => setShowIndex(false)}
        />
      )}
    </div>
  );
};

export default BaiyeReport;
